<template>
  <div :class="classes()">
    <template v-if="step <= 2">
      <div :class="`${el}__content`">
        <Container>
          <Row>
            <Col :xs="12" :l="6" :lOffset="3">
              <Step1 v-if="step === 1" :el="el" :href="href" @onTrackingClick="trackClick" />
              <Step2 v-if="step === 2" :el="el" :href="href" @onTrackingClick="trackClick" />
            </Col>
          </Row>
        </Container>
      </div>
      <div :class="`${el}__backgroundImageWrapper`">
        <Illustration
          :class="`${el}__backgroundImage`"
          name="title-5"
          :brush="1"
          :illuScale="1.2"
        />
      </div>
    </template>
    <template v-else>
      <Container>
        <Row>
          <Col :xs="12" :l="4" :lOffset="4">
            <Copy element="div" :staticClass="`${el}__question`" color="primaryBlue" centered>
              {{ $t('intro.question') }} {{ step - 2 }} / 2
            </Copy>
            <Step3 v-if="step === 3" :el="el" :href="href" @onTrackingClick="trackClick" />
            <Step4
              v-if="step === 4"
              :el="el"
              :href="href"
              :back="back"
              :loading="loading"
              @click="dispatch"
              @onTrackingClick="trackClick"
            />
            <Copy v-if="error" color="supportDanger" size="l" centered>
              {{ $t('intro.error') }}
            </Copy>
          </Col>
        </Row>
      </Container>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import Illustration from '@/objects/Illustration';
import Container from '@/objects/Container';
import Copy from '@/objects/Copy';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';
import { ROUTE_INTRO, ROUTE_DASHBOARD } from '@/constants/routes';
import {
  ROUTE_API_UPDATE_CARE_LEVEL_DATA,
  ROUTE_API_UPDATE_CARE_PROFILE,
} from '@/constants/apiRoutes';

import Step1 from './subcomponents/Step1';
import Step2 from './subcomponents/Step2';
import Step3 from './subcomponents/Step3';
import Step4 from './subcomponents/Step4';

import { mapFieldsHealth, mapFieldsUser } from '@/store/mapFields';

import { TASKS_UPDATE_CURRENT_TASK } from '@/store/actions/tasks';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';

import careLevelMixin from '@/mixins/careLevel';

export default {
  name: 'Intro',
  props: {},
  mixins: [careLevelMixin],
  components: {
    Illustration,
    Container,
    Copy,
    Row,
    Col,
    Step1,
    Step2,
    Step3,
    Step4,
  },
  computed: {
    ...mapFieldsHealth([
      'dependentPerson',
      'careLevelSituation',
      'careLevel',
      'careLevelDate',
      'healthUuid',
    ]),

    ...mapFieldsUser(['user']),

    step() {
      const { step } = this.$route.params;
      return parseInt(step, 10) || 1;
    },
    href() {
      return `${ROUTE_INTRO}/${this.step + 1}`;
    },
    back() {
      return `${ROUTE_INTRO}/${this.step - 1}`;
    },
  },
  created() {
    this.trackClick('', 'wizard_step_show');
    if (this.step > 1) {
      this.$router.push(ROUTE_INTRO);
    }
    this.redirectToDashboard();
  },
  data() {
    return {
      loading: false,
      error: false,
      ROUTE_INTRO,
      el: 'v-intro',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  watch: {
    dependentPerson() {
      this.redirectToDashboard();
    },

    step() {
      if (this.error && this.step > 3) {
        this.error = false;
      }
      this.trackClick('', 'wizard_step_show');
    },
  },
  methods: {
    redirectToDashboard() {
      if (this.step === 1 && this.dependentPerson) {
        this.$router.push(ROUTE_DASHBOARD);
      }
    },

    dispatch() {
      this.loading = true;

      /**
       * Um die zu verstehen, wie Werte vergeben werde, bitte das Store-Modul "health" beachten.
       * Dort sind die States beschrieben.
       */

      const intro = {
        personInNeedOfCareUuid: this.healthUuid,
        careLevel: this.careLevelSituation !== 2 ? this.careLevel : 0,
        careLevelDate: this.careLevelDate,
        careLevelSituation: this.careLevelSituation,
      };

      Vue.axios
        .post(ROUTE_API_UPDATE_CARE_LEVEL_DATA, { careLevelWizard: intro })
        .then((response) => {
          const { status } = response;
          if (status === 200) {
            Vue.axios
              .post(ROUTE_API_UPDATE_CARE_PROFILE, {
                careProfile: {
                  isProfileOwner: this.dependentPerson === 'me' ? 1 : 0,
                  firstname: this.dependentPerson === 'me' ? this.user.firstname : null,
                  lastname: this.dependentPerson === 'me' ? this.user.lastname : null,
                },
                personInNeedOfCareUuid: this.healthUuid,
              })
              .then(() => {
                this.loading = false;
                this.setTasks();
                this.$router.push(ROUTE_DASHBOARD);
              })
              .catch(this.catch);
          }
          this.trackClick('_btn-' + this.$t('intro.step4.button'), 'button');
        })
        .catch(this.catch);
    },

    catch() {
      this.loading = false;
      this.error = true;
      this.$router.push(`${ROUTE_INTRO}/3`);
    },

    setTasks() {
      if (this.careLevelSituation !== 2) {
        /**
         * Wenn Pflegegrad vorhanden: P3-20 -> this.careLevelSituation === 0
         * Wenn Pflegegrad beantragt: P2-30 -> this.careLevelSituation === 1
         * Wenn kein Pflegegrad: P1-10 -> this.careLevelSituation === 2
         */

        const taskID = this.careLevelSituation === 0 ? 'P3-20' : 'P2-40';

        this.$store.dispatch(TASKS_UPDATE_CURRENT_TASK, taskID);
      }
    },

    trackClick(label, element) {
      const product = 'Intro';
      let stepLabel = '';
      let action = '-click';

      if (element === 'wizard_step_show') {
        action = '';
        stepLabel = '-wizard-step-' + this.step;
        switch (this.step) {
          case null:
            stepLabel = stepLabel + '-Willkommen';
            break;
          case 2:
            stepLabel = stepLabel + '-kennenlernen';
            break;
          case 3:
            stepLabel = stepLabel + '-Frage 1/2';
            break;
          case 4:
            stepLabel = stepLabel + '-Frage 2/2';
            break;
        }
      }
      const labelText = product + stepLabel + label;

      createTrackingData(product, labelText, element, action);
    },
  },
};
</script>
